import { legacyHassTag } from '../hass/hassLegacyTag.js';
import ObjectGroup from './ObjectGroup';
import HaspObject from './HaspObject';
import { getActiveTheme } from './properties/ConfigManager.js';
import { getObjectSchema, isHaspProperty, OBJECT_PROP_SKIP } from './properties/v2/wireFormat.js';
import { COMMON_PROPS } from './properties/v2/shared.js';
import { computeEffectiveStyleEntries, applyEffectiveStyleEntry } from './properties/v2/exportOptimizer.js';
import { effectiveObjectPropertyDefault } from './properties/objectPropertyStatus.js';
import { normalizeStretchMode } from './properties/v2/stretchMode.js';
import { canonicalizeGpioBindings, hasGpioBindings } from '../gpio/GpioBindingModel.js';

/**
 * Expand the legacy Designer slider/switch/arc knob padding into the four firmware
 * attributes. Direct pad_*20 entries are authoritative and therefore are not
 * overwritten when a legacy shorthand and a new side value coexist.
 */
export function expandLegacyKnobPadding(objectData, value) {
    const pad = Number(value) / 2;
    objectData.pad_top20 ??= pad;
    objectData.pad_right20 ??= pad;
    objectData.pad_left20 ??= pad;
    objectData.pad_bottom20 ??= pad;
    return objectData;
}

export default class ObjectExport extends ObjectGroup {
    constructor(config) {
        super(config);
    }

    /**
     * HaspObject may override this function to add additional export properties
     */
    objectExport() { }

    /**
     * determine if HaspObjects need export, like matrixbtn does not
     * @param {*} prop 
     */
    needsExport(prop) {
        if (((this.defaults[prop] !== undefined && this[hasprop] !== this.defaults[prop]) ||
            (obj_theme[prop]) !== undefined && this[hasprop] !== obj_theme[prop]) &&
            !(this.obj === 'arc' && prop === 'rotation')) {  //arc does not export rotation
            objectData[prop] = this[hasprop];
        }
    }

    /**
     * Her the actual export for the HaspObject is done
     * @param {*} page 
     * @param {*} exportData 
     * @param {*} includeHass 
     * @returns 
     */
    export(page, exportData, includeHass, isDesignSave = false) {
        // console.log('EXPORT')
        // console.log(this);
        let objectData = [];
        const obj_theme = getActiveTheme()[this.obj] ?? {};

        if (!this.no_export) {
            objectData = {
                id: this.haspid,
                obj: this.obj,
            };
            if (this.comment !== undefined && this.comment !== '') objectData.comment = this.comment;
            if (this.custom_component_id)   objectData.custom_component_id   = this.custom_component_id;
            if (this.custom_component_name) objectData.custom_component_name = this.custom_component_name;
            if (this.obj !== 'tab') {
                objectData.x = Math.round(this.x());
                objectData.y = Math.round(this.y());
            }
            if (this.obj === 'qrcode') {
                objectData.text = this.text;
                objectData.size = Math.round(this.width());
            } else if (this.obj !== 'tab') {
                objectData.w = Math.round(this.width());
                objectData.h = Math.round(this.height());
            }
            // tab: no w/h/size/x/y — text is set by HaspTab.objectExport, parentid below

            // if (!this.hasp_enabled) objectData.enabled = this.hasp_enabled;

            //check for parent_id — tab → tabview, objects in tab → tab
            const parent = this.getParent();
            if (parent instanceof HaspObject) {
                if (!parent.isType('page') && !parent.isType('template_page')) {
                    objectData.parentid = parent.haspid;
                }
            }
            // Use the v2 schema to drive which properties are exported for this
            // object type. Falls back to an empty schema for any object type
            // that does not yet have a v2 schema.
            const _schema = getObjectSchema(this.obj) ?? { parts: {}, properties: {} };

            // Part-scoped style properties, every semantic state included —
            // computed entirely by the export optimizer (computeEffectiveStyleEntries),
            // which itself contains zero cascade/default comparison logic: it
            // asks node.resolveStyleTrace() (StyleResolver.resolveTrace) for
            // each part/prop/state whether an explicit override exists and
            // whether it's redundant (identical to what the cascade beneath
            // it already produces), emitting only the ones that actually
            // change the effective rendered result. The firmware wire key
            // (bg_color10, bg_color1001, …) is only ever computed here, for
            // the exported key — this file is the OpenHASP exporter, the one
            // place allowed to know it.
            for (const entry of computeEffectiveStyleEntries(this, _schema)) {
                applyEffectiveStyleEntry(this, objectData, obj_theme, entry);
            }

            // Top-level (non-style) properties — always were, and remain, flat
            // instance properties; unaffected by the part/style refactor above.
            for (const [prop, meta] of Object.entries({ ...COMMON_PROPS, ..._schema.properties })) {
                if (OBJECT_PROP_SKIP.has(prop)) continue;
                // Read the value once. If it is a Konva prototype method (function) the
                // plain property was never set on this node — treat it as "not set".
                const propVal = this.getConfiguredProperty?.(prop) ?? this[prop];
                if (typeof propVal === 'function') continue;
                // Same theme-then-schema default rule the Property Editor's
                // override indicator uses (objectPropertyStatus.js) — one
                // definition of "still on its default" for both.
                const effectiveDefault = effectiveObjectPropertyDefault(this, prop);
                const notDefault = effectiveDefault !== undefined && propVal !== undefined && propVal !== effectiveDefault;
                if (!this.hasProperty(prop)) continue;

                if (isHaspProperty(_schema, prop)) {
                    const hasprop = 'hasp_' + prop;
                    const hasp_effectiveDefault = effectiveObjectPropertyDefault(this, prop);
                    const haspValue = this.getConfiguredProperty?.(hasprop) ?? this[hasprop];
                    if (hasp_effectiveDefault !== undefined && haspValue !== hasp_effectiveDefault &&
                        !(this.obj === 'arc' && prop === 'rotation')) {
                        objectData[prop] = haspValue;
                    }
                } else if (notDefault) {
                    if (prop === 'text' || prop === 'value_str' || prop === 'template') {
                        if (propVal !== '') objectData[prop] = propVal;
                    } else {
                        objectData[prop] = propVal;
                    }
                }
            }

            // stretch_mode is an editor-only property and is therefore excluded
            // from firmware JSONL. Design saves must retain it, however, so a
            // project round-trip does not silently change an object's mode.
            // Normalize here to preserve the legacy full_stretch alias while
            // keeping the schema options/default as the single authority.
            if (isDesignSave && this.hasProperty('stretch_mode')) {
                const stretchMode = normalizeStretchMode(
                    _schema,
                    this.getConfiguredProperty?.('stretch_mode') ?? this.stretch_mode,
                );
                if (stretchMode !== undefined) objectData.stretch_mode = stretchMode;
            }
            // Firmware cannot handle gauge line_count=1. Resolve this on the
            // export record without changing the live Designer object.
            if (this.obj === 'gauge' && this.line_count === 1) objectData.line_count = 0;

            // if (this.enabled === false) objectData.enabled = false;
            // if (this.swipe) objectData.swipe = 1;

            // Per-event actions are exported as the Designer's own structured
            // list — `actions: [{ id, event, actions: Action[] }]`, the exact
            // shape node.actions holds (see ConfigManager). This is project
            // data: `resourceId` is an Audio Resource uuid, and `command` may
            // be the Designer's typed `audio.play` sentinel rather than a
            // firmware command string. The legacy openHASP `action` map
            // ({event: "<command string>"}) is derived from this, once, at
            // device-export time by Exporter._processActions() — the same
            // boundary at which `image` uuids become `L:/…` paths.
            // Persistence is data-driven: any object carrying configured
            // handlers saves them, regardless of object type/capability flags.
            // Event availability remains schema-driven in the editor.
            if (Array.isArray(this.actions)) {
                const actions = JSON.parse(JSON.stringify(this.actions));
                if (actions.length > 0) objectData.actions = actions;
            }
            if (this.has_group && this.groupid && this.groupid !== '') {
                objectData.groupid = this.groupid;
            }

            // GPIO is a firmware-native provider contract, independent of
            // Home Assistant. Keep it as a top-level field in both design
            // saves and runtime JSONL; includeHass must never gate it.
            const gpioBindings = canonicalizeGpioBindings(this.gpio_bindings);
            if (hasGpioBindings(gpioBindings)) objectData.gpio_bindings = gpioBindings;
            //data values
            // if (this.hasProperty('min') && this.min !== this.defaults['min']) objectData.min = this.min;
            // if (this.hasProperty('max') && this.max !== this.defaults['max']) objectData.max = this.max;
            // if (this.hasProperty('val') && this.val !== this.defaults['val']) objectData.val = this.val;

            // padding is exportSpecial (expands to pad_top/right/left/bottom) —
            // handled here, outside the generic part-property loop above. The
            // asymmetric per-part expansion (halved on the knob, left-only on
            // item_buttons) is genuine firmware behaviour, not something to
            // generalize away — this file is the OpenHASP exporter, the one
            // place allowed to hardcode wire keys. Whether to emit at all is
            // still decided exclusively by resolveStyleTrace() (explicit +
            // non-redundant), same rule as every other style property.
            //
            // A part may ALSO declare the four per-side overrides (see
            // shared.js's PADDING_SIDES). Those are plain properties whose wire
            // keys are exactly these pad_* keys, so the generic loop above has
            // already emitted every side the user overrode explicitly — the
            // shorthand expansion here must therefore only fill sides that are
            // still absent, never overwrite one (that is what keeps an overall
            // padding and a conflicting side value from both being exported).
            const mainPaddingTrace = this.resolveStyleTrace('main', 'padding', 'default');
            if (mainPaddingTrace.explicit && !mainPaddingTrace.redundant) {
                const mainPadding = mainPaddingTrace.value;
                objectData.pad_top ??= mainPadding;
                objectData.pad_right ??= mainPadding;
                objectData.pad_left ??= mainPadding;
                objectData.pad_bottom ??= mainPadding;
            }
            const knobPaddingTrace = this.resolveStyleTrace('knob', 'padding', 'default');
            if (knobPaddingTrace.explicit && !knobPaddingTrace.redundant) {
                expandLegacyKnobPadding(objectData, knobPaddingTrace.value);
            }
            const itemButtonsPaddingTrace = this.resolveStyleTrace('item_buttons', 'padding', 'default');
            if (itemButtonsPaddingTrace.explicit && !itemButtonsPaddingTrace.redundant) {
                const itemButtonsPadding = itemButtonsPaddingTrace.value;
                // objectData.pad_top40 = itemButtonsPadding;
                // objectData.pad_right40 = itemButtonsPadding;
                objectData.pad_left40 = itemButtonsPadding;
                // objectData.pad_bottom40 = itemButtonsPadding;
            }
            // checkbox's bullet (indicator) part — real firmware pad_all
            // (lv_theme_hasp.c's cb_bullet style), unlike the asymmetric
            // per-part expansions above. Uses bullet's own '10' wire suffix
            // (see checkbox.js), not the generic indicator table, since
            // schemaValidator requires every part to declare its own.
            // The bullet's axis overrides come FIRST: each expands to its own
            // axis' pair of wire keys, and the `padding` shorthand below then
            // fills only whichever sides are still absent (`??=`) — so an
            // overall value and an axis override can never emit conflicting
            // keys for the same side. Both are exportSpecial (1 value → 2 or 4
            // keys), which is why neither reaches the generic loop above.
            const bulletAxes = [
                { prop: 'pad_hor', wireKeys: ['pad_left10', 'pad_right10'] },
                { prop: 'pad_ver', wireKeys: ['pad_top10', 'pad_bottom10'] },
            ];
            for (const { prop, wireKeys } of bulletAxes) {
                const trace = this.resolveStyleTrace('bullet', prop, 'default');
                if (!trace.explicit || trace.redundant) continue;
                for (const wireKey of wireKeys) objectData[wireKey] ??= trace.value;
            }
            const bulletPaddingTrace = this.resolveStyleTrace('bullet', 'padding', 'default');
            if (bulletPaddingTrace.explicit && !bulletPaddingTrace.redundant) {
                const bulletPadding = bulletPaddingTrace.value;
                objectData.pad_top10 ??= bulletPadding;
                objectData.pad_right10 ??= bulletPadding;
                objectData.pad_left10 ??= bulletPadding;
                objectData.pad_bottom10 ??= bulletPadding;
            }

            // "hass_config":{"entityid":"switch.so_gang_relay_1","event":"down","service":"switch","action":"toggle"}
            // if (this.hasProperty('entityid') && this.hass_entityid !== undefined || this.hass_entityid === '') {
            //   var hasConfig = { entityid: this.hass_entityid, event: this.hass_event, service: this.hass_service, action: this.hass_action };
            //   objectData['hass_config'] = hasConfig;
            // }

        } else {
            return;
        }

        this.objectExport(page, objectData);

        // Derive the openHASP `tag` property from entity bindings in hass_config.
        // `tag` is a native firmware property — it travels with events to MQTT so
        // tag is only exported when there is exactly one unique entity across all
        // bindings. With multiple entities the YAML uses an explicit list and tag
        // would be misleading — it can only carry one value.
        // A migrated object keeps the tag of its former legacy rows (hass_config.tag).
        if (this.hass_config) {
            const tag = legacyHassTag(this.hass_config) ?? this.hass_config.tag;
            if (typeof tag === 'string' && tag) objectData.tag = tag;
        }

        if (includeHass && this.hass_config) {
            // Strip pre-generated JSONL parameters — they are regenerated at YAML
            // export time from the template page, so storing them wastes space and
            // risks serving stale data.
            const events = (this.hass_config.events || []).map(ev => {
                if (ev.service === 'openhasp' && ev.action === 'command') {
                    const cleanData = (ev.data || []).filter(d => d.key !== 'parameters');
                    return { ...ev, data: cleanData };
                }
                return ev;
            });
            objectData.hass_config = { ...this.hass_config, events };
        }
        exportData.push(objectData);
        this.getChildren().forEach((element) => {
            if (element instanceof HaspObject) {
                element.export(page, exportData, includeHass, isDesignSave);
            }
        });
    }

}
